import { alpha, Box, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { get, isEmpty, isNil, map, pickBy, upperFirst } from 'lodash';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import { TREEMAP_PALLET_COLOR_HS_CODE } from 'src/components/HighChart/Treemap/config';
import ProductBreakdownSkeleton from 'src/features/CompanyDetail/HSCode/ProductBreakdownSkeleton';
import NoDataSearchCompany from 'src/features/CompanyProfiles/NoDataSearchCompany';
import FilterChart from 'src/features/CompanyTradeData/common/FilterChart';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { useQueryCompanyTradeDataHSCode } from 'src/services/queries/useQueryTradeDataWithoutHSCode';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy } from 'src/utils/constants/enum';

const ProductBreakdown = () => {
  const [unitBy, setUnitBy] = useState(KeyFilterChartTradeDataUnitBy.value_usd);
  const { id } = useParams();
  const queryParams = useQueryParams();
  const { end_date, start_date, keyword, is_buyer } = queryParams;
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: start_date ?? dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    toTime: end_date ?? dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
  });
  const params = pickBy(
    {
      ...queryParams,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      offset: 0,
      limit: 9,
      id: id,
      order: 'desc',
      sort_by: unitBy,
    },
    (value) => !isNil(value) && value !== '',
  );

  const { data, isFetching } = useQueryCompanyTradeDataHSCode(params);

  const renderChart = () => {
    if (isFetching) return <ProductBreakdownSkeleton />;

    if (!isFetching && isEmpty(data?.data?.children))
      return (
        <WhiteBoxContent sx={{ display: 'grid', placeItems: 'center' }} height={600}>
          <NoDataSearchCompany
            title={`This company does not ${queryParams?.is_buyer === 'false' ? 'export' : 'import'} , therefor it has no information`}
            hideTip
            description=""
          />
        </WhiteBoxContent>
      );

    return map(data?.data?.children, (item, i) => (
      <Grid item xs={12} sm={6} md={4} lg={3} key={item.hscode}>
        <HSCodeTree unitBy={unitBy} level={0} data={item} parentColorIndex={i} />
      </Grid>
    ));
  };

  return (
    <WhiteBoxContent>
      <FilterChart
        selectedRangeTime={selectedRangeTime}
        onSelectTimeRange={({ fromTime, toTime }) => {
          setSelectedRangeTime({
            fromTime: fromTime,
            toTime: toTime,
          });
        }}
        title={'HS Code Proportion Chart'}
      />

      <Box mt={3} />

      <Grid container spacing={3}>
        {renderChart()}
      </Grid>
    </WhiteBoxContent>
  );
};

export default ProductBreakdown;

const HSCodeTree = ({
  data,
  level = 0,
  parentColorIndex = 0,
  unitBy,
}: {
  data: TypeCompanyTradeDataHSCodeChildren;
  level: number;
  parentColorIndex: number;
  unitBy: KeyFilterChartTradeDataUnitBy;
}) => {
  const backgroundColor = alpha(TREEMAP_PALLET_COLOR_HS_CODE[parentColorIndex], 0.2 * (level + 1));
  return (
    <Box
      borderRadius={2}
      mb={1}
      display="flex"
      flexDirection="column"
      flex={1}
      height={'100%'}
      bgcolor={backgroundColor}
      p={1}
    >
      <Tooltip
        placement="top"
        title={
          <Stack spacing={1} p={1}>
            <Typography color={'text.secondary'} variant="subtitle1">
              HS Code <span style={{ color: 'white' }}>{data.hscode}</span>
            </Typography>
            <Typography color={'text.secondary'} variant="subtitle1">
              {upperFirst(unitBy)} <span style={{ color: 'white' }}>{get(data, unitBy)}</span>
            </Typography>
            <Divider />
            <Typography variant="body2" color={'inherit'}>
              {data.description}
            </Typography>
          </Stack>
        }
      >
        <Typography mb={1} textAlign="center" variant="subtitle1" color={level === 0 ? 'text.primary' : 'white'}>
          {data.hscode}
        </Typography>
      </Tooltip>
      {map(data.children, (child, i) => (
        <HSCodeTree
          unitBy={unitBy}
          key={child.hscode}
          data={child}
          level={level + 1}
          parentColorIndex={parentColorIndex}
        />
      ))}
    </Box>
  );
};
